/**
 * Ad attribution capture (UTM params + ad-platform click ids), forwarded to
 * studio.inducelabs.ai on signup so the app can attach it to conversion events.
 * Belt-and-suspenders alongside the GA4 cross-domain linker in index.html.
 */

export type AttributionParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  wbraid?: string;
  gbraid?: string;
  msclkid?: string;
};

const STORAGE_KEY = 'induce_attribution';
const PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'wbraid',
  'gbraid',
  'msclkid',
] as const satisfies readonly (keyof AttributionParams)[];

/** Capture attribution params from the URL on arrival. No-op if none present (keeps any prior value). */
export function captureAttributionOnLoad(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const found: AttributionParams = {};
    for (const key of PARAM_KEYS) {
      const value = params.get(key);
      if (value) found[key] = value;
    }
    if (Object.keys(found).length === 0) return;

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(found));
  } catch {
    // Fail-open
  }
}

/** Session-scoped attribution captured on arrival, filtered to known campaign keys. */
export function getStoredAttribution(): AttributionParams {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const result: AttributionParams = {};
    for (const key of PARAM_KEYS) {
      const value = parsed[key];
      if (typeof value === 'string' && value) result[key] = value;
    }
    return result;
  } catch {
    return {};
  }
}

/** Append the session's stored attribution params onto an outbound URL (e.g. the studio signup link). */
export function appendAttributionToUrl(url: string): string {
  try {
    const attribution = getStoredAttribution();
    const entries = Object.entries(attribution);
    if (entries.length === 0) return url;

    const target = new URL(url);
    for (const [key, value] of entries) {
      target.searchParams.set(key, value);
    }
    return target.toString();
  } catch {
    return url;
  }
}
